/**
 * Maps Lexical-generated class names and inline styles back to attributes
 * that Lexical's importDOM understands natively.
 *
 * Problem: when Lexical exports HTML it uses class names like "editor-text-bold"
 * and inline styles like "white-space: pre-wrap". When re-importing, $generateNodesFromDOM
 * doesn't recognize these — it looks for <strong>, <em>, <u> tags or specific
 * style properties like font-weight: bold.
 */
export function preprocessDOM(dom: Document): Document {
  const removeEditorClasses = (el: Element) => {
    const classList = Array.from(el.classList);
    classList.forEach((className) => {
      if (className.startsWith("editor-")) {
        el.classList.remove(className);
      }
    });

    if (el.classList.length === 0) {
      el.removeAttribute("class");
    }
  };

  // ── 1. Map class names → semantic tags ──────────────────────────────────────
  // IMPORTANT: a text span can contain MULTIPLE format classes at once
  // (e.g. "editor-text-bold editor-text-italic").
  // Replacing by selector one-by-one drops remaining classes.
  dom.querySelectorAll("[class*='editor-text-']").forEach((el) => {
    const htmlEl = el as HTMLElement;
    const classList = Array.from(htmlEl.classList);

    const formats: Array<"strong" | "em" | "u" | "s" | "code"> = [];
    if (classList.includes("editor-text-bold")) formats.push("strong");
    if (classList.includes("editor-text-italic")) formats.push("em");
    if (classList.includes("editor-text-underline")) formats.push("u");
    if (classList.includes("editor-text-strikethrough")) formats.push("s");
    if (classList.includes("editor-text-underlineStrikethrough")) {
      formats.push("u", "s");
    }
    if (classList.includes("editor-text-code")) formats.push("code");

    if (formats.length === 0) return;

    // Preserve non-editor classes and meaningful inline styles.
    const remainingClasses = classList.filter(
      (name) => !name.startsWith("editor-text-"),
    );
    const styleText = htmlEl.getAttribute("style") || "";

    // Build wrapper tree: <span style... class...><strong><em>...</em></strong></span>
    // span is added only when needed to carry styles/custom classes.
    let top: HTMLElement | null = null;
    let cursor: HTMLElement | null = null;

    const appendWrapper = (tag: string): HTMLElement => {
      const wrapper = dom.createElement(tag);
      if (!top) {
        top = wrapper;
      } else if (cursor) {
        cursor.append(wrapper);
      }
      cursor = wrapper;
      return wrapper;
    };

    const shouldCreateSpanContainer =
      !!styleText.trim() || remainingClasses.length > 0;

    if (shouldCreateSpanContainer) {
      const span = appendWrapper("span");
      if (styleText.trim()) {
        span.setAttribute("style", styleText);
      }
      if (remainingClasses.length > 0) {
        span.setAttribute("class", remainingClasses.join(" "));
      }
    }

    formats.forEach((tag) => appendWrapper(tag));

    if (!top || !cursor) return;

    cursor.append(...Array.from(el.childNodes));
    el.replaceWith(top);
  });

  // ── 1b. Map inline style text formatting → semantic tags ────────────────────
  // Example: style="text-decoration: underline; font-style: italic; ..."
  // Lexical import is more reliable with semantic tags (<u>, <em>, <strong>, <s>)
  // than class/style-only formatting.
  dom.querySelectorAll("[style]").forEach((el) => {
    const htmlEl = el as HTMLElement;
    const style = htmlEl.style;

    const fontWeight = style.fontWeight?.trim().toLowerCase() || "";
    const numericFontWeight = Number.parseInt(fontWeight, 10);
    const hasBold =
      fontWeight === "bold" ||
      fontWeight === "bolder" ||
      (!Number.isNaN(numericFontWeight) && numericFontWeight >= 600);

    const fontStyle = style.fontStyle?.trim().toLowerCase() || "";
    const hasItalic = fontStyle === "italic" || fontStyle === "oblique";

    const textDecoration = (
      style.textDecorationLine ||
      style.textDecoration ||
      ""
    )
      .trim()
      .toLowerCase();
    const hasUnderline = textDecoration.includes("underline");
    const hasStrike = textDecoration.includes("line-through");

    const formats: Array<"strong" | "em" | "u" | "s"> = [];
    if (hasBold && !["STRONG", "B"].includes(el.tagName))
      formats.push("strong");
    if (hasItalic && !["EM", "I"].includes(el.tagName)) formats.push("em");
    if (hasUnderline && el.tagName !== "U") formats.push("u");
    if (hasStrike && !["S", "DEL", "STRIKE"].includes(el.tagName)) {
      formats.push("s");
    }

    if (formats.length === 0) return;

    const originalChildren = Array.from(el.childNodes);
    if (originalChildren.length === 0) return;

    let rootWrapper: HTMLElement | null = null;
    let cursor: HTMLElement | null = null;

    formats.forEach((tag) => {
      const wrapper = dom.createElement(tag);
      if (!rootWrapper) {
        rootWrapper = wrapper;
        el.append(wrapper);
      } else if (cursor) {
        cursor.append(wrapper);
      }
      cursor = wrapper;
    });

    if (cursor) {
      cursor.append(...originalChildren);
    }
  });

  // ── 2. Clean up inline styles Lexical doesn't need ──────────────────────────

  // Remove "white-space: pre-wrap" — Lexical adds this on export but
  // doesn't need it on import. Leaving it causes importDOM to treat
  // the span as a styled node and drop the actual meaningful styles.
  dom.querySelectorAll("span, p, div, strong, em, u, s").forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.removeProperty("white-space");

    // If span has no remaining meaningful styles and no classes, unwrap it
    // so Lexical doesn't create a redundant TextNode wrapper
    if (
      el.tagName === "SPAN" &&
      !htmlEl.style.cssText.trim() &&
      el.className === ""
    ) {
      el.replaceWith(...Array.from(el.childNodes));
    }
  });

  // ── 3. Map editor-paragraph → <p> (already a <p> but strip the class) ───────
  dom.querySelectorAll(".editor-paragraph").forEach((el) => {
    removeEditorClasses(el);
    // Preserve dir attribute for LTR/RTL
  });

  // ── 4. Map editor-heading-h1/h2/h3 → proper heading tags ───────────────────
  ["h1", "h2", "h3", "h4", "h5", "h6"].forEach((tag) => {
    dom.querySelectorAll(`.editor-heading-${tag}`).forEach((el) => {
      const heading = dom.createElement(tag);
      heading.innerHTML = el.innerHTML;
      const htmlEl = el as HTMLElement;
      if (htmlEl.style.cssText) heading.style.cssText = htmlEl.style.cssText;
      heading.style.removeProperty("white-space");
      el.replaceWith(heading);
    });
  });

  // ── 5. Map editor-listitem / editor-list-ul / editor-list-ol ────────────────
  dom.querySelectorAll(".editor-listitem").forEach((el) => {
    removeEditorClasses(el);
  });
  dom.querySelectorAll(".editor-list-ul, .editor-list-ol").forEach((el) => {
    removeEditorClasses(el);
  });

  // ── 6. Map editor-link → <a> ─────────────────────────────────────────────────
  dom.querySelectorAll(".editor-link").forEach((el) => {
    removeEditorClasses(el);
  });

  return dom;
}
